/**
 * Device Flow Callback Page
 * 
 * Handles the callback from Amazon Federate after device flow authentication.
 * Processes the authorization code and redirects to the appropriate page.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column, Text, Loader } from '@amzn/meridian';
import { useAppDispatch } from '../../../store/store';
import { getUser } from '../../../store/userSlice';
import { authManager } from '../../../services/authManager';

const DeviceCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const callbackProcessedRef = useRef(false);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate processing
      if (callbackProcessedRef.current) {
        console.log('[DeviceCallback] Callback already processed, skipping');
        return;
      }

      // Check if we're actually on the callback URL with query parameters
      const currentUrl = window.location.href;
      const currentPath = window.location.pathname;
      
      console.log('[DeviceCallback] Current URL:', currentUrl);
      console.log('[DeviceCallback] Current path:', currentPath);
      
      // Check for malformed URLs (like /device_callback/http://localhost:4321/)
      if (currentPath.includes('/device_callback/http') || currentPath.length > 20) {
        console.log('[DeviceCallback] Detected malformed callback URL, redirecting to home');
        navigate('/', { replace: true });
        return;
      }
      
      if (currentPath !== '/device_callback') {
        console.log('[DeviceCallback] Not on callback path, redirecting to home');
        navigate('/', { replace: true });
        return;
      }
      
      // For device flow, we expect either query parameters or hash parameters
      if (!window.location.search && !window.location.hash) {
        console.log('[DeviceCallback] No callback parameters found, redirecting to home');
        navigate('/', { replace: true });
        return;
      }

      // Mark as processing to prevent duplicate calls
      callbackProcessedRef.current = true;

      try {
        console.log('[DeviceCallback] Processing device flow callback');
        setStatus('processing');

        // Handle the authentication callback
        const result = await authManager.handleAuthenticationCallback();
        
        if (result.success) {
          console.log('[DeviceCallback] Callback successful, updating user state');
          setStatus('success');
          
          // Update the Redux store with the new user information
          try {
            await dispatch(getUser()).unwrap();
            console.log('[DeviceCallback] User state updated successfully');
          } catch (userError) {
            console.error('[DeviceCallback] Failed to update user state:', userError);
            // Continue with redirect even if user state update fails
          }
          
          // After a successful login the user must always land on the Home page.
          sessionStorage.removeItem('deviceFlowOrigin');
          sessionStorage.removeItem('deviceFlowClientRequestId');

          console.log('[DeviceCallback] Login successful, redirecting to /home');

          // Use replace to avoid back button issues and redirect immediately
          navigate('/home', { replace: true });
          
        } else {
          // Authentication callback failed
          console.error('[DeviceCallback] Authentication callback failed:', result.error);
          setStatus('error');
          setErrorMessage(result.error || 'Authentication callback failed');
          
          // For authentication failures, redirect to home page to show the sign-in interface
          // This handles cases where the backend returns 403 for invalid tokens
          redirectTimeoutRef.current = setTimeout(() => {
            navigate('/', { replace: true });
          }, 1500);
        }
        
      } catch (error) {
        console.error('[DeviceCallback] Callback processing failed:', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
        
        // For any callback processing errors, redirect to home page
        // This ensures users don't get stuck on the callback page
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      }
    };

    // Set up a safety timeout to prevent users from getting stuck on this page
    const safetyTimeout = setTimeout(() => {
      console.log('[DeviceCallback] Safety timeout triggered, redirecting to home');
      navigate('/', { replace: true });
    }, 10000); // 10 second timeout

    handleCallback();

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(safetyTimeout);
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array to run only once

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <>
            <Loader size="large" type="circular" />
            <Text>Processing authentication...</Text>
            <Text color="secondary">
              Please wait while we complete your sign-in.
            </Text>
          </>
        );
        
      case 'success':
        return (
          <>
            <Text>✅ Authentication successful!</Text>
            <Text color="secondary">
              Redirecting you to the application...
            </Text>
          </>
        );
        
      case 'error':
        return (
          <>
            <Text>❌ Authentication failed</Text>
            <Text color="secondary">
              {errorMessage.includes('403') || errorMessage.includes('Unauthorized') 
                ? 'Your authentication token is not valid. You will be redirected to sign in again.'
                : errorMessage}
            </Text>
            <Text color="secondary">
              Redirecting you to the application...
            </Text>
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <Column
      width="100vw"
      height="100vh"
      alignmentHorizontal="center"
      alignmentVertical="center"
      spacing="medium"
    >
      {renderContent()}
    </Column>
  );
};

export default DeviceCallbackPage;